import type { NarrativeBeat, RunDetailResponse, RunEventResponse } from "@/api/types";

export interface ScenarioRevealMeta {
    badge: string;
    title: string;
    summary: string;
    flareLines: string[];
}

export interface ScenarioUpcomingIntel {
    atWeek: number;
    title: string;
    kind: string;
    summary: string;
    prep: string[];
}

interface ScenarioIntroBeat {
    type: NarrativeBeat["type"];
    speaker?: string | null;
    text: string;
    audioSrc?: string | null;
    mood: string;
    variant?: NarrativeBeat["variant"];
    variantLabel?: string | null;
    variantCopy?: string | null;
    illustrationSrc?: string | null;
    illustrationAlt?: string | null;
}

/**
 * 2026-04-23: ScenarioDetail 우측 패널의 "핵심 성격" 중복 문제 해결용.
 *   왼쪽 카드 subtitle 과 동일 문장이 오른쪽에 한 번 더 붙던 것을, 이 playstyle 필드로
 *   교체해 "이 시나리오는 어떤 시스템 기반으로 플레이되고 어떤 재미가 있는가" 를 전달.
 *
 * 형식:
 *   focus   — 플레이의 주 축 (관리 대상, 주요 레이어)
 *   fun     — 이 시나리오의 독특한 재미/의사결정 톤
 *   caution — 조심해야 할 감각적 리스크 / 실패 패턴
 */
export interface ScenarioPlaystyle {
    focus: string;
    fun: string;
    caution: string;
}

interface ScenarioPresentationDefinition {
    reveal: ScenarioRevealMeta;
    intel: ScenarioUpcomingIntel[];
    introTitle: string;
    introMessage: string;
    introBeats: (run: RunDetailResponse) => ScenarioIntroBeat[];
    playstyle?: ScenarioPlaystyle;
}

function resolveFrontierRole(tags: string[]): string {
    if (tags.includes("role:frontline")) return "전열";
    if (tags.includes("role:quartermaster")) return "병참";
    if (tags.includes("role:scout")) return "정찰";
    return "미정";
}

const SCENARIO_PRESENTATIONS: Record<string, ScenarioPresentationDefinition> = {
    "frontier-vanguard": {
        playstyle: {
            focus: "원정 6축(원정 소모·보급선·사기·변경 신뢰·추모 무게·보급 빚) 을 한 순씩 저울질하는 '상태 유지' 시나리오.",
            fun: "정복·화의·수호귀환·공허귀환·선너머 묻힘 — 다섯 결말 중 지금 기울어진 방향을 중간에 읽으며 궤적을 꺾는 맛.",
            caution: "한두 축이 임계를 넘으면 연쇄 악화. 보급 빚·추모 무게는 한 번 쌓이면 쉽게 안 빠집니다.",
        },
        reveal: {
            badge: "신규 개방",
            title: "변경 토벌 원정",
            summary: "지도의 끝에서 사람과 장부와 약속이 함께 닳아 가는 계절. 전투보다 귀환이 더 어렵고, 승리보다 책임이 오래 남는 이야기가 열립니다.",
            flareLines: [
                "보급선과 병사 사기, 변경의 신뢰가 하나의 숨처럼 함께 흔들립니다.",
                "앞으로 중요한 것은 얼마나 크게 이겼는가가 아니라, 누구를 끝까지 데리고 돌아왔는가입니다.",
            ],
        },
        intel: [
            { atWeek: 12, title: "첫 행군 전야", kind: "장면 예고", summary: "실전이 시작되기 전, 원정대 전체가 처음으로 긴장감을 공유하는 밤입니다.", prep: ["체력과 방어 기반", "초반 역할 선택의 의미 파악"] },
            { atWeek: 21, title: "행군 명령", kind: "장 전환", summary: "준비를 끝내고 본격적인 변경 행군이 시작됩니다. 여기서부터는 피로가 진짜 비용이 됩니다.", prep: ["원정 소모 관리", "정찰과 제작 비중 준비"] },
            { atWeek: 32, title: "벼랑끝 전투", kind: "주요 교전", summary: "정면 돌파, 지형 활용, 병력 보존 중 무엇을 우선할지 고르는 협곡 전투입니다.", prep: ["완력·기술력·방어력", "병사 사기 손실 대비"] },
            { atWeek: 46, title: "원정 도박꾼의 제안", kind: "보급 분기", summary: "빠른 길과 위험한 빚이 함께 찾아옵니다. 이후 보급 부채의 출발점이 될 수 있습니다.", prep: ["속력·기술력", "정규 보급과 비공식 거래 판단"] },
            { atWeek: 49, title: "거점 구축 명령", kind: "장 전환", summary: "전진만으로 버티지 못하는 구간에 들어서며, 거점과 주민 관계가 중요해집니다.", prep: ["변경 신뢰", "보급선 안정도", "중장기 운영 감각"] },
            { atWeek: 56, title: "고대 유적 발견", kind: "탐사 분기", summary: "자원, 봉인, 심층 탐사 중 무엇을 택할지에 따라 이후 봉인 관련 사건이 열릴 수 있습니다.", prep: ["정신력과 지력", "위험 감수 여부 판단"] },
            { atWeek: 73, title: "최종 결착 브리핑", kind: "장 전환", summary: "이제부터는 누적된 약속과 손실이 결말을 결정하기 시작합니다.", prep: ["병사 사기", "변경 신뢰", "보급 부채 정리"] },
            { atWeek: 80, title: "묻지 못한 불의 밤", kind: "추모 컷신", summary: "마지막 결전 앞, 먼저 쓰러진 이름들을 어떻게 기억할지 정하는 밤입니다.", prep: ["정신력 기반", "추모 부담 체감", "감정과 냉정 사이의 선택"] },
            { atWeek: 88, title: "정복과 화의", kind: "최종 결단", summary: "정복, 화의, 귀환 중 무엇을 택할지 결정하는 마지막 갈림길입니다.", prep: ["원정 상태 3축 점검", "후방 방어와 명분 회수"] },
        ],
        introTitle: "변경 토벌 원정",
        introMessage: "살아 돌아오는 것조차 업적이 되는 계절이 시작됩니다.",
        introBeats: (run) => {
            const role = resolveFrontierRole(run.scenarioState?.outcomeTags ?? []);
            const roleLine = role === "전열"
                ? "이번 이름은 가장 먼저 맞는 줄에 기록되었습니다. 영광은 커질 수 있지만, 함께 돌아올 이름의 무게도 그만큼 커집니다."
                : role === "병참"
                    ? "이번 이름은 뒤에서 모두를 살리는 장부 곁에 기록되었습니다. 칼보다 늦게 보이는 선택이지만, 더 오래 남는 책임이기도 합니다."
                    : role === "정찰"
                        ? "이번 이름은 누구보다 먼저 길을 보는 자리에 기록되었습니다. 먼저 본다는 건 먼저 위험해진다는 뜻이기도 합니다."
                        : "이번 이름은 아직 한 줄의 공백처럼 보이지만, 곧 원정대 안에서 어떤 방식으로든 무게를 갖게 됩니다.";
            return [
                { type: "narration", variant: "briefing", variantLabel: "원정 서막", variantCopy: "왕국의 가장 바깥선에서 시작되는 장기 원정의 배경을 들려주는 장면입니다.", text: "왕국의 동쪽 끝, 지도가 희미해지는 자리에서는 늘 먼저 짐수레가 끊기고 그다음에 사람의 이름이 사라진다. 변경 토벌 원정은 단지 괴물을 쫓기 위해서가 아니라, 더 바깥으로 밀려나 버린 질서의 선을 다시 묶기 위해 열렸다.", audioSrc: "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-01-borderline.wav", mood: "marching", illustrationSrc: "/art/illustrations/scenarios/frontier-vanguard/frontier-intro-muster.png", illustrationAlt: "변경 끝자락의 습한 새벽, 집결한 원정대와 원정 헌장을 읽는 장교들" },
                { type: "dialogue", variant: "briefing", speaker: "나레이션", text: "이번 원정은 단순한 토벌이 아니다. 보급선이 끊긴 밤을 버티고, 병사들의 사기가 내려앉는 새벽을 견디고, 주민들이 끝내 믿어 줄 약속 하나를 마지막까지 놓지 않는 일이다. 칼은 앞을 가르지만, 원정의 무게는 늘 뒤에 남은 사람 수로 계산된다.", audioSrc: "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-02-expedition-weight.wav", mood: "grave", illustrationSrc: "/art/illustrations/scenarios/frontier-vanguard/frontier-intro-march.png", illustrationAlt: "차가운 하늘 아래 짐수레와 병사, 정찰선이 함께 길게 늘어선 변경 행군" },
                { type: "narration", variant: "briefing", text: "이 이야기의 끝은 하나가 아니다. 정복으로 밀어붙일 수도 있고, 화의로 선을 다시 그을 수도 있으며, 살아 있는 이름을 더 많이 데리고 돌아오는 쪽을 승리라 부를 수도 있다. 여기서는 결말보다 그 결말을 만든 계절의 무게가 더 오래 남는다.", audioSrc: "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-03-many-endings.wav", mood: "frontier", illustrationSrc: "/art/illustrations/scenarios/frontier-vanguard/frontier-intro-march.png", illustrationAlt: "먼 능선과 회색 하늘 사이를 길게 건너는 원정대의 행렬" },
                { type: "dialogue", variant: "briefing", speaker: "나레이션", text: roleLine, audioSrc: role === "전열" ? "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-04-role-frontline.wav" : role === "병참" ? "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-04-role-quartermaster.wav" : role === "정찰" ? "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-04-role-scout.wav" : "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-04-role-default.wav", mood: "solemn" },
                { type: "narration", variant: "decision", variantLabel: "출정의 의미", variantCopy: "왜 이 이야기를 하는지, 무엇을 위해 끝까지 버텨야 하는지를 마지막으로 새기는 장면입니다.", text: `${run.characterName}의 이름은 이제 보고서의 한 줄이 아니라, 원정 전체의 계절 속으로 들어간다. 여기서 중요한 것은 얼마나 크게 이겼는가보다, 얼마나 오래 버텼는가, 그리고 끝내 누구를 데리고 돌아왔는가다. 변경은 늘 승리보다 귀환을 더 늦게 허락한다.`, audioSrc: "/audio/narration/scenarios/frontier-vanguard/intro/frontier-vanguard-intro-05-closing-vow.wav", mood: "decisive", illustrationSrc: "/art/illustrations/scenarios/frontier-vanguard/frontier-intro-vow.png", illustrationAlt: "출정 직전 낮은 불가 곁에서 서로를 바라보는 원정대와 귀환의 맹세" },
            ];
        },
    },
    "arena-crown": {
        playstyle: {
            focus: "완력·속력·매력(평판) 세 축을 번갈아 올리며 예선→본선→결승으로 올라가는 '토너먼트' 시나리오.",
            fun: "검 실력과 군중 앞 태도가 동시에 점수에 반영되어, 이기는 법뿐 아니라 '어떻게 이기는지' 가 결말을 바꾸는 맛.",
            caution: "후원/평판 관리를 소홀히 하면 실력이 충분해도 결승에서 편이 갈립니다. 매 주차 외출/사교 비중을 놓치지 마세요.",
        },
        reveal: {
            badge: "대표 이야기",
            title: "투기장 왕관전",
            summary: "환호와 혈통, 연습과 계산이 같은 모래 위에 얹히는 계절. 승리는 단검보다 평판을 더 날카롭게 만듭니다.",
            flareLines: [
                "대련과 명성, 후원과 결투가 한 시즌의 왕관을 향해 겹쳐집니다.",
                "이곳에서 중요한 것은 한 번의 승리보다, 끝까지 누구의 이름으로 서는가입니다.",
            ],
        },
        intel: [
            { atWeek: 12, title: "첫 예선", kind: "장면 예고", summary: "정식 무대에 이름을 올리기 전, 첫 예선에서 투기장의 공기를 배우게 됩니다.", prep: ["기본 전투 기반", "명성의 첫 인상"] },
            { atWeek: 28, title: "후원가의 제안", kind: "평판 분기", summary: "검만으로는 못 오르는 자리에서, 누구의 이름을 빌릴지 고민하게 됩니다.", prep: ["매력과 평판", "명예와 실리 사이 판단"] },
            { atWeek: 56, title: "왕관전 준결", kind: "주요 교전", summary: "한 번의 실수가 가문 전체의 얼굴로 남는 준결승 무대입니다.", prep: ["완력·속력", "군중 앞 압박 대응"] },
            { atWeek: 88, title: "왕관전 결승", kind: "최종 결단", summary: "승리를 택할지, 품위를 지킬지, 다른 전설을 남길지 마지막으로 가르는 결투입니다.", prep: ["최고점 스탯", "평판과 후원 정리"] },
        ],
        introTitle: "투기장 왕관전",
        introMessage: "모래 위에 남는 것은 발자국이 아니라 이름입니다.",
        introBeats: () => [
            { type: "narration", variant: "briefing", variantLabel: "입장의 계절", variantCopy: "가문과 이름이 군중 앞에 처음 걸리는 장면입니다.", text: "왕관전의 계절이 열리면, 투기장의 모래는 칼날보다 먼저 이름의 무게를 가른다. 이곳에서 사람들은 강함만 보지 않는다. 누구의 이름으로 서는지, 어떤 태도로 이기는지, 그리고 패배를 어떤 얼굴로 받아들이는지까지 함께 기억한다.", mood: "marching" },
            { type: "dialogue", variant: "briefing", speaker: "나레이션", text: "검은 결국 팔과 허리에서 나가지만, 투기장은 언제나 관객의 눈과 후원가의 계산 위에서 돌아간다. 이 계절은 싸움의 이야기이면서 동시에 평판과 욕망의 이야기다.", mood: "grave" },
            { type: "narration", variant: "decision", variantLabel: "왕관의 뜻", variantCopy: "승리보다 오래 남는 이름의 모양을 새기는 장면입니다.", text: "여기서 왕관은 머리에 얹는 금속보다 더 무겁다. 끝까지 모래를 버틴 사람의 얼굴, 그리고 그 얼굴 뒤에 남을 이름이 이 이야기의 진짜 결말이 된다.", mood: "decisive" },
        ],
    },
    "magitower-summit": {
        playstyle: {
            focus: "연구(지력·정신) 누적과 공개 변론, 봉인 서고 접근의 세 축을 함께 굴리는 '지적 정치' 시나리오.",
            fun: "논문 점수만으로는 안 되는 회의장 — 누구와 어떤 자리에 서 있었는지가 함께 기록되어 최종 표결을 가르는 맛.",
            caution: "금단 지식을 얻으면 순간 강해지지만 대가가 오래 남습니다. 봉인 vs 공개의 대가 계산이 핵심 운영 포인트.",
        },
        reveal: {
            badge: "대표 이야기",
            title: "마탑 정상회의",
            summary: "논문과 예법, 연구실 정치와 금단의 지식이 같은 회랑에서 맞부딪히는 계절입니다.",
            flareLines: [
                "지식은 늘 빛나 보이지만, 마탑에서는 언제나 누가 먼저 말할지부터가 권력입니다.",
                "한 장의 문서와 한 번의 침묵이 모두 결말을 바꿀 수 있습니다.",
            ],
        },
        intel: [
            { atWeek: 10, title: "첫 공개 변론", kind: "장면 예고", summary: "학자와 귀족 앞에서 자신의 이름과 논지를 처음 걸게 됩니다.", prep: ["지력과 정신", "말의 단정함"] },
            { atWeek: 26, title: "봉인 서고 접근권", kind: "탐사 분기", summary: "지식을 얻는 대신 무엇을 잃을지 계산해야 하는 순간입니다.", prep: ["연구 기반", "위험 감수 판단"] },
            { atWeek: 52, title: "회의장 표결", kind: "정치 분기", summary: "논문보다 편이 더 크게 작용하는, 회의장의 냉정한 표결입니다.", prep: ["매력과 권위", "누구와 서 있을지"] },
            { atWeek: 72, title: "정상회의 최종안", kind: "최종 결단", summary: "지식을 공개할지 봉인할지, 혹은 다른 이름으로 넘길지 결정하는 마지막 장면입니다.", prep: ["지력·정신", "정치적 기반"] },
        ],
        introTitle: "마탑 정상회의",
        introMessage: "한 줄의 문서가 한 계절의 권력을 바꿉니다.",
        introBeats: () => [
            { type: "narration", variant: "briefing", variantLabel: "회랑의 숨", variantCopy: "지식과 권력이 한 홀에 모이는 계절의 서막입니다.", text: "마탑의 회랑은 언제나 조용하지만, 그 침묵은 결코 비어 있지 않다. 여기서는 발소리보다 먼저 논문 제목이 기억되고, 인사보다 먼저 어느 연구실에 속했는지가 계산된다.", mood: "frontier" },
            { type: "dialogue", variant: "briefing", speaker: "나레이션", text: "정상회의의 계절은 진실이 드러나는 자리가 아니라, 어떤 진실이 살아남을 수 있는지를 겨루는 무대다. 한 장의 문서와 한 번의 침묵이 모두 결말을 바꾼다.", mood: "grave" },
            { type: "narration", variant: "decision", variantLabel: "지식의 값", variantCopy: "배움이 언제 권력이 되는지를 새기는 장면입니다.", text: "이 이야기의 끝은 정답을 얻는 일이 아니라, 끝내 어떤 지식을 자신의 이름으로 남길 것인지 정하는 일이 된다.", mood: "decisive" },
        ],
    },
    "skyrail-grandprix": {
        playstyle: {
            focus: "속력·기술력과 기체 정비 상태를 함께 관리하는 '랩 단위 누적' 시나리오.",
            fun: "한 랩의 타임과 기체 진동이 바로 순위로 환산되며, 후원과 라이벌이 레이스 밖에서도 판세를 흔드는 맛.",
            caution: "제작·정비를 게을리하면 최종 주행에서 한 번의 진동이 그대로 추락으로 이어집니다. 속도만 올리면 안 됩니다.",
        },
        reveal: {
            badge: "대표 이야기",
            title: "천궤도 그랑프리",
            summary: "속도와 기술, 기체와 명예가 같은 철로 위를 가르는 계절. 한 번의 진동이 순위를 바꾸고, 한 번의 오차가 추락이 됩니다.",
            flareLines: [
                "조종과 정비, 후원과 라이벌리가 모두 같은 랩 안에서 엮입니다.",
                "이곳의 승리는 단순히 빨랐다는 말보다 더 오래 남습니다.",
            ],
        },
        intel: [
            { atWeek: 8, title: "면허 실기", kind: "장면 예고", summary: "공식 레이스에 들어서기 전, 하늘 철로 위에서 첫 판정을 받습니다.", prep: ["속력·기술", "기체 안정"] },
            { atWeek: 24, title: "예선 리그", kind: "주요 교전", summary: "실수 하나가 바로 순위와 후원으로 이어지는 첫 리그입니다.", prep: ["속력·기술", "소모 관리"] },
            { atWeek: 46, title: "철로 사고 수습", kind: "보급 분기", summary: "승부보다 유지와 수리가 더 중요해지는 구간에 들어섭니다.", prep: ["제작과 정비", "자금 여력"] },
            { atWeek: 64, title: "그랑프리 최종 주행", kind: "최종 결단", summary: "속도를 끝까지 밀지, 완주를 택할지, 명예의 다른 얼굴을 고를지 마지막으로 가릅니다.", prep: ["속력 최고점", "기체 안전선"] },
        ],
        introTitle: "천궤도 그랑프리",
        introMessage: "하늘 철로 위에서는 한 번의 진동도 전설이 됩니다.",
        introBeats: () => [
            { type: "narration", variant: "briefing", variantLabel: "철로의 계절", variantCopy: "속도와 기술이 한 이름을 세우는 시즌의 서막입니다.", text: "천궤도 그랑프리는 단지 빠른 사람을 뽑는 대회가 아니다. 어느 기체가 끝까지 버티는지, 어느 조종이 떨림을 제어하는지, 그리고 누가 마지막 랩의 공기를 끝내 자기 쪽으로 돌리는지가 함께 기록된다.", mood: "marching" },
            { type: "dialogue", variant: "briefing", speaker: "나레이션", text: "속도는 늘 찬란하게 보이지만, 실제로 무대를 떠받치는 것은 정비, 연료, 후원, 그리고 추락을 견딜 각오다.", mood: "grave" },
            { type: "narration", variant: "decision", variantLabel: "속도의 얼굴", variantCopy: "가장 빠른 것과 끝까지 남는 것의 차이를 새기는 장면입니다.", text: "이 계절이 끝난 뒤 남는 것은 기록표 한 줄만이 아니다. 누구의 기체가, 누구의 숨이, 어떤 하늘에서 전설이 되었는지가 함께 남는다.", mood: "decisive" },
        ],
    },
    "sunken-cathedral": {
        playstyle: {
            focus: "정신·의지 축의 안정성을 지키면서 탐사와 봉인 결단을 저울질하는 '심층 탐사' 시나리오.",
            fun: "구원과 금단이 같은 층에 묻혀 있어, 누구를 구하고 무엇을 깨우지 않을지 정하는 도덕적 선택의 밤이 반복되는 맛.",
            caution: "깊이 들어갈수록 정신이 흔들리고, 한 번 깨운 것은 다시 잠들지 않습니다. 매 탐사 전 의지 상태 확인 필수.",
        },
        reveal: {
            badge: "대표 이야기",
            title: "침수 성당의 성가",
            summary: "잠긴 성당의 기도와 금단의 기억이 밀물처럼 되돌아오는 계절. 구원과 집착이 같은 물결 위를 걷습니다.",
            flareLines: [
                "기도와 탐사, 구조와 봉인이 같은 물가에서 뒤엉킵니다.",
                "이곳에서는 누구를 구했는가와 무엇을 깨웠는가가 같은 기록으로 남습니다.",
            ],
        },
        intel: [
            { atWeek: 14, title: "조수의 부름", kind: "장면 예고", summary: "성당의 안쪽이 처음으로 너를 부르는 밤입니다.", prep: ["정신과 의지", "탐사 준비"] },
            { atWeek: 32, title: "침수 회랑", kind: "탐사 분기", summary: "더 깊이 잠길수록 구원과 금단의 경계가 흐려집니다.", prep: ["지력·정신", "봉인 감수"] },
            { atWeek: 58, title: "성가의 저편", kind: "추모 컷신", summary: "물 아래 묻힌 이름들을 무엇으로 기억할지 묻는 장면입니다.", prep: ["정신 기반", "추모와 구조의 균형"] },
            { atWeek: 78, title: "마지막 기도", kind: "최종 결단", summary: "성당을 닫을지, 여전히 열어 둘지, 혹은 다른 이름으로 남길지 정하는 끝입니다.", prep: ["의지·지력", "봉인 여부 판단"] },
        ],
        introTitle: "침수 성당의 성가",
        introMessage: "잠긴 기도는 언제나 가장 늦게 돌아옵니다.",
        introBeats: () => [
            { type: "narration", variant: "briefing", variantLabel: "물 아래의 홀", variantCopy: "침수 성당의 계절과 그 불길한 부름을 여는 장면입니다.", text: "성당은 이미 오래전에 물 아래로 기울었지만, 성가는 아직 완전히 죽지 않았다. 밀물이 드는 밤이면 금 간 기둥과 잠긴 제단 사이로 오래된 기도가 다시 떠오르고, 사람들은 그 소리를 구원이라 부르거나 금단이라 부른다.", mood: "frontier" },
            { type: "dialogue", variant: "briefing", speaker: "나레이션", text: "이곳의 문제는 단지 깊이 들어갈 수 있느냐가 아니다. 끝까지 들어갔을 때 무엇을 데리고 돌아오게 되는지가 늘 더 큰 문제다.", mood: "grave" },
            { type: "narration", variant: "decision", variantLabel: "기도의 값", variantCopy: "구원과 봉인이 같은 물결 위를 걷는 이야기를 새기는 장면입니다.", text: "이 이야기는 깊은 곳으로 내려가는 모험이면서 동시에, 무엇을 깨우지 않은 채 남길지를 정하는 이야기이기도 하다.", mood: "decisive" },
        ],
    },
};

export function getScenarioRevealMeta(scenarioId: string): ScenarioRevealMeta | null {
    return SCENARIO_PRESENTATIONS[scenarioId]?.reveal ?? null;
}

export function getScenarioPlaystyle(scenarioId: string): ScenarioPlaystyle | null {
    return SCENARIO_PRESENTATIONS[scenarioId]?.playstyle ?? null;
}

export function getScenarioUpcomingIntel(scenarioId: string, currentWeek: number): ScenarioUpcomingIntel | null {
    const intel = SCENARIO_PRESENTATIONS[scenarioId]?.intel ?? [];
    return intel.find(entry => entry.atWeek > currentWeek) ?? null;
}

export function getScenarioCurrentIntel(scenarioId: string, currentWeek: number): ScenarioUpcomingIntel | null {
    const intel = SCENARIO_PRESENTATIONS[scenarioId]?.intel ?? [];
    return intel.find(entry => entry.atWeek === currentWeek) ?? null;
}

export function formatScenarioIntelEta(currentWeek: number, atWeek: number): string {
    const diff = atWeek - currentWeek;
    if (diff <= 0) return "이번 주";
    if (diff === 1) return "다음 주";
    return `${diff}주 뒤`;
}

export function isHighImpactIntel(kind: string): boolean {
    return kind.includes("결단")
        || kind.includes("교전")
        || kind.includes("분기")
        || kind.includes("최종")
        || kind.includes("추모");
}

export function buildScenarioIntroEvents(run: RunDetailResponse): RunEventResponse[] {
    const def = SCENARIO_PRESENTATIONS[run.scenario.id];
    if (!def) return [];
    return [{
        weekNo: 1,
        eventOrder: 0,
        sourceType: "scenario",
        eventType: "SCENARIO_INTRO",
        title: def.introTitle,
        message: def.introMessage,
        sceneBeats: def.introBeats(run).map(beat => ({
            type: beat.type,
            speaker: beat.speaker ?? null,
            text: beat.text,
            audioSrc: beat.audioSrc ?? null,
            mood: beat.mood,
            variant: beat.variant ?? "standard",
            illustrationSrc: beat.illustrationSrc ?? null,
            illustrationAlt: beat.illustrationAlt ?? null,
            variantLabel: beat.variantLabel ?? null,
            variantCopy: beat.variantCopy ?? null,
        })),
        statDelta: {},
        resourceDelta: {},
        rewardPayload: {},
        choices: [],
        eventId: null,
        destinyId: null,
        phase: "SYSTEM",
        outcomeBand: null,
    }];
}
